import _ from 'lodash';
import { extractCritical } from './emotion';
import { UploadingAndUploadedFile } from '../utils/useProject/useProject';
import { crush as minifyHtml } from 'html-crush';
import { compileTemplate } from './compileTemplate';
import { CompileFunction } from './types';
import { getChecksum } from '../utils/getChecksum';
import fromP5 from 'hast-util-from-parse5';
import { toHtml } from 'hast-util-to-html';
import parse5 from 'parse5';
// @ts-expect-error Could not find a declaration file for module '@cheapsteak/hast-util-find-and-replace'.
import findAndReplace from '@cheapsteak/hast-util-find-and-replace'; // can switch back to normal hast-util-find-and-replace after https://github.com/syntax-tree/hast-util-find-and-replace/issues/1 is resolved
// eslint-disable-next-line @typescript-eslint/no-var-requires
const h = require('hastscript');

const defaultNonBreakingTerms = [
  /STELARA® I.V./gi,
  /invega trinza/gi,
  /invega sustenna/gi,
  ' :',

  // Make any spaces between a word and a number a non-breaking space
  // `[A-Za-zÀ-ÖØ-öø-ÿ]` is like if `[\w]` matched characters with accents
  // TODO: look into https://davidwalsh.name/regex-accented-letters
  /([\d]+)\s+([A-Za-zÀ-ÖØ-öø-ÿ])/g,
  /([A-Za-zÀ-ÖØ-öø-ÿ<>≤≥])\s+([\d]+)/g,

  // (n = 3) shouldn't wrap
  /\s+=\s+/g,

  /« +/g,
  / +»/g,
];

const NBSP = '\u00a0';

export const processUsingAST = (
  html: string,
  nonBreakingTerms: Array<string | RegExp>,
) => {
  // parsing html into parse5 AST document
  const parse5ASTDocument = parse5.parse(html);
  // converting parse5 AST format to unified AST format
  const domTree = fromP5(parse5ASTDocument);

  const htmlOutput = _.flow([
    ...nonBreakingTerms.map((term) => {
      return (tree: typeof domTree) => {
        return findAndReplace(tree, term, function (match: string) {
          return match.replace(/ /g, NBSP);
        });
      };
    }),

    // replace all hyphens with non-breaking hyphen
    (tree: typeof domTree) =>
      findAndReplace(
        tree,
        '-',
        function (match: string) {
          return match.replace(/-/g, '\u2011');
        },
        {
          // don't wrap <sup>s around things that are already wrapped in <sup>
          // or is in preview text
          ignore: function (node: any) {
            return (
              shouldSkipReplacingNode(node) ||
              ['title', 'script', 'style', 'svg', 'math', 'sup'].includes(
                node.tagName,
              )
            );
          },
        },
      ),

    // don't allow whitespace to wrap trademarks
    (tree: typeof domTree) =>
      findAndReplace(
        tree,
        /((?:([a-zA-Z])+)?®)/g,
        ($0: string) => {
          return h(
            'span',
            { style: 'white-space: nowrap;' },
            $0.replace(/ +/g, NBSP),
          );
        },
        {
          ignore: shouldSkipReplacingNode,
        },
      ),

    // wrap <sup>s around things that should be superscript
    (tree: typeof domTree) =>
      findAndReplace(
        tree,
        /(®|†|‡|¥|¶|€)/g,
        ($0: string, $1: string) => {
          return h('sup', $1);
        },
        {
          // don't wrap <sup>s around things that are already wrapped in <sup>
          // or is in preview text
          ignore: function (node: any) {
            return (
              shouldSkipReplacingNode(node) ||
              ['title', 'script', 'style', 'svg', 'math', 'sup'].includes(
                node.tagName,
              )
            );
          },
        },
      ),

    // wrap <span>s around non-breaking-spaces for outlook mobile
    (tree: typeof domTree) => {
      return findAndReplace(
        tree,
        /(\u00a0|&nbsp;)/g,
        ($0: string, $1: string) => {
          return h('span', $1);
        },
        {
          ignore: shouldSkipReplacingNode,
        },
      );
    },

    // replace '<'s with html entity
    // this is unnecessary, hast-util-to-html already escapes & and <
    // (tree: typeof domTree) => findAndReplace(tree, /</, '&lt;'),

    (tree) => {
      return toHtml(tree, {
        // other wise `style="background-image: url('images/bg-topcap.png');"` gets turned into
        // `background-image: url(&#x27;images/bg-topcap.png&#x27;);`
        allowDangerousCharacters: true,
        allowParseErrors: true,
        characterReferences: {
          useShortestReferences: true,
        },
      });
    },
  ])(domTree);

  return htmlOutput;
};

const processVeevaTags = (input: string) => {
  const veevaReplaceList: Array<[RegExp, string]> = [
    [/##accLname##/g, '[Last Name]'],
    [/##accFname##/g, '[First Name]'],
    [/{{userName}}/g, '[Rep Name]'],
    [/{{accLname}}/g, '[Last Name]'],
  ];

  const processInterpolationTags = (stringMaybeContainingTags: string) => {
    let stringToReturn = stringMaybeContainingTags;

    veevaReplaceList.forEach(
      ([regex, replacement]) =>
        (stringToReturn = stringToReturn.replace(regex, replacement)),
    );
    return stringToReturn;
  };

  const processDropdowns = (dropdownOptionsString: string) => {
    const options = dropdownOptionsString.split('|');
    return `<span
    style="
      display: inline-block;
      background-color: #f0f0f010;
      border: 1px dashed #ccc;
      border-bottom: 0;
    ">
      ${options
        .map(
          (optionText) =>
            `<span
          style="
            padding: 4px 4px;
            border-bottom: 1px dashed #ccc;
            display: block;
          "
        >${processInterpolationTags(optionText)}</span>`,
        )
        .join('\n')}
    </span>`;
  };

  return processInterpolationTags(
    input.replace(/{{customText\[(.+?)\]}}/g, (match) => {
      const searchResults = /customText\[(.+?)\]/.exec(match);
      return processDropdowns(searchResults![1]);
    }),
  );
};

interface MJMLCompileError {
  formattedMessage: string;
  line: number;
  message: string;
  tagName: string;
}

export const compile: CompileFunction = async ({
  src,
  language,
  files,
  previewVeevaTags,
  remoteImages,
  shouldMinify,
  isForRenderedPreview,
  shouldAddChecksum,
}) => {
  if (!src) return await { html: src, errors: [], matchedImages: [] };

  try {
    const {
      html,
      errors,
      ejsOutput,
      nonBreakingTerms,
      removeVMLTags,
      postProcessFunction,
    } = await compileTemplate(src, language, isForRenderedPreview);

    if (errors.length && process.env.NODE_ENV !== 'test') {
      console.log('compile errors', {
        mjmlCompileInput: ejsOutput,
        errors: errors.map((error: MJMLCompileError) => ({
          ...error,
          surroundingLines: ejsOutput
            .split('\n')
            .map((line, lineIndex) => {
              // apparently mjml error numbers start at 1
              const lineNumber = lineIndex + 1;
              return {
                lineNumber,
                isErrorLineNumber: lineNumber === error.line,
                line,
              };
            })
            .slice(error.line - 10, error.line + 10),
        })),
      });
    }

    const matchedImages: UploadingAndUploadedFile[] = [];

    return {
      matchedImages,
      errors,
      html: [
        (input: string) =>
          input
            // Remove end of text(ETX) characters from html, this renders as a space
            .replace(/\x03/g, ' ') // eslint-disable-line no-control-regex

            // remove invisible unicode 'END OF TEXT' characters that get copied over from illustrator text
            // these ends up being rendered as a rectangle in outlook
            .replace(/\u0003/g, ''), // eslint-disable-line no-control-regex,

        // mjml has a typo in their skeleton file that uses an incorrect conditional comment
        // fix it for them manually until this pr is released
        // https://github.com/mjmlio/mjml/pull/1958
        (input: string) =>
          input.replace(/<!--\[if !mso\]><!-- -->/g, '<!--[if !mso]><!-->'),

        // inject styles generated by emotion
        (input: string) =>
          input.replace(
            /<\/head>/i,
            `
              <style data-emotion-global type="text/css">
              ${extractCritical(html)
                .css // outlook mobile does not like `}}`. it will strip out everything after it in the same <style> block
                .replace(/}/g, '}\n')

                // veeva's buggy asset replacement performs uri-encoding on things that are near/inside a background-image with an url
                // this causes e.g. `linear-gradient(to bottom,black 0)` to become `linear-gradient(to%20bottom,black%200)` and is invalid
                // and <style> blocks containing invalid css gets removed by Android's gmail
                // this replacement forces a new line between `url(...), linear-gradient(...)`
                .replace(/\),/g, '),\n')}
              </style>
              </head> 
            `,
          ),
        (input: string) =>
          processUsingAST(input, [
            ...nonBreakingTerms,
            ...defaultNonBreakingTerms,
          ]),

        // XXX(Chang): Veeva's customText cannot have double quotes inside
        // https://crmhelp.veeva.com/doc/Content/CRM_topics/Multichannel/ApprovedEmail/ManageCreateContent/CreatingContent/ConfigTokens.htm
        // Our AST serialization uses double quotes to serialize attribute values
        // Changing serialization options is an option, although tradeoffs include
        //   - Monaco's default html syntax highlighter not recognizing single-quotes as an attribute delimiter
        //   - Users who aren't aware of Veeva's customText deficiencies bewildered by the quote choice (since single quote is less common)
        (input: string) =>
          input.replace(/{{customText\[(.+?)\]}}/g, (match) =>
            match.replace(/"/g, "'"),
          ),

        (input: string) =>
          input
            .replace(/<sup>/g, `<sup style="line-height:0px;font-size:60%;">`)
            // .replace(/\u00a0/g, '&nbsp;')
            // .replace(/\u200C/g, '&zwnj;')
            .replace(/©/g, '&copy;'),
        (input: string) => {
          if (!previewVeevaTags) {
            return input;
          }
          return processVeevaTags(input);
        },
        (input: string) => {
          // match on double quotes
          // TODO: make these smarter
          return (
            input
              .replace(
                /"(\.\/)?(images\/[^"]+)/g,
                (fullMatch, _firstGroup, relativePathWithoutDot) => {
                  const image = files.find(
                    (image) =>
                      image.relativePath.replace(/^\.\//, '') ===
                      relativePathWithoutDot,
                  );

                  if (image?.remoteUrl) {
                    matchedImages.push(image);
                    if (remoteImages) {
                      return `"${encodeURI(image.remoteUrl)}`;
                    }
                  }
                  return fullMatch;
                },
              )
              // match on single quotes
              .replace(
                /'(\.\/)?(images\/[^']+)/g,
                (fullMatch, _firstGroup, relativePathWithoutDot) => {
                  const image = files.find(
                    (image) =>
                      image.relativePath.replace(/^\.\//, '') ===
                      relativePathWithoutDot,
                  );

                  if (image) {
                    matchedImages.push(image);
                    if (remoteImages) return `'${image.remoteUrl}`;
                  }
                  return fullMatch;
                },
              )
              // match on no quotes
              .replace(
                /\((\.\/)?(images\/[^)]+)/g,
                (fullMatch, _firstGroup, relativePathWithoutDot) => {
                  const image = files.find(
                    (image) =>
                      image.relativePath.replace(/^\.\//, '') ===
                      relativePathWithoutDot,
                  );

                  if (image) {
                    matchedImages.push(image);
                    if (remoteImages) return `('${image.remoteUrl}'`;
                  }
                  // TODO: escape `'` in url path?
                  return `${fullMatch.replace(/^\(/, "('")}'`;
                },
              )
          );
        },
        // remove all VML code
        (input: string) =>
          removeVMLTags ? input.replace(/(<|<\/)v:.*(\n|$)/g, '') : input,
        (input: string) => {
          if (shouldMinify) {
            return minifyHtml(input, {
              lineLengthLimit: 500,
              removeIndentations: true,
              removeLineBreaks: false,
            }).result;
          } else {
            return input;
          }
        },
        (input: string) => {
          if (shouldAddChecksum) {
            return input + `<!--${getChecksum(input)}-->`;
          }
          return input;
        },
        postProcessFunction,
      ].reduce((str, func) => func(str), html),
    };
  } catch (e) {
    if (e instanceof Error) {
      console.trace('error compiling to html', e.message);
    }
    throw e;
  }
};

const regexDisplayNone = /display:\s*none/;
const regexTransparent = /opacity:\s*0/;
const regexMaxWidth0 = /max-width:\s*0/;

function shouldSkipReplacingNode(node: any) {
  const nodeStyle = node.properties?.style;
  const isProbablyPreheaderWrapper =
    regexDisplayNone.test(nodeStyle) &&
    regexTransparent.test(nodeStyle) &&
    regexMaxWidth0.test(nodeStyle);
  return (
    isProbablyPreheaderWrapper ||
    node.properties?.className?.includes('hidden-preview-block') ||
    node.properties?.className?.includes('mc-skip-wrap') ||
    node.properties?.className?.includes('mc-skip')
  );
}
